import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

interface Props {
  centerLat?: number;
  centerLng?: number;
  radiusM?: number;
  onChange: (lat: number, lng: number, radiusM: number) => void;
}

// UB center
const UB_LAT = 47.8864;
const UB_LNG = 106.9057;

type PickStep = "idle" | "center_set" | "radius_set";

export const DeliveryZoneMapPicker: React.FC<Props> = ({
  centerLat,
  centerLng,
  radiusM,
  onChange,
}) => {
  const mapRef = useRef<L.Map | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const circleRef = useRef<L.Circle | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const stepRef = useRef<PickStep>("idle");
  const centerRef = useRef<L.LatLng | null>(null);
  // Prevents the map click that Leaflet fires after mouseup from resetting the center
  const suppressNextClickRef = useRef(false);

  const [hint, setHint] = useState("Click on the map to set the zone center");
  const [info, setInfo] = useState<{ lat: number; lng: number; radius: number } | null>(null);

  const hintText = (step: PickStep) => {
    if (step === "idle") return "Click on the map to set the zone center";
    if (step === "center_set") return "Click again to set the radius — drag the circle edge to resize";
    return "Click anywhere to move the center, or drag the circle edge to resize";
  };

  // Init map
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      center: [centerLat ?? UB_LAT, centerLng ?? UB_LNG],
      zoom: 12,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap contributors",
    }).addTo(map);

    mapRef.current = map;

    // If editing existing zone — draw it immediately
    if (centerLat && centerLng && radiusM) {
      const latlng = L.latLng(centerLat, centerLng);
      centerRef.current = latlng;
      drawMarker(map, latlng);
      drawCircle(map, latlng, radiusM);
      stepRef.current = "radius_set";
      setHint(hintText("radius_set"));
      setInfo({ lat: centerLat, lng: centerLng, radius: radiusM });
    } else {
      setHint(hintText("idle"));
    }

    map.on("click", handleMapClick);

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const drawMarker = (map: L.Map, latlng: L.LatLng) => {
    if (markerRef.current) markerRef.current.remove();
    const icon = L.divIcon({
      className: "",
      html: `<div style="
        width:14px;height:14px;
        border-radius:50%;
        background:#e53935;
        border:3px solid #fff;
        box-shadow:0 2px 6px rgba(0,0,0,0.4);
      "></div>`,
      iconSize: [14, 14],
      iconAnchor: [7, 7],
    });
    markerRef.current = L.marker(latlng, { icon }).addTo(map);
  };

  const drawCircle = (map: L.Map, center: L.LatLng, radius: number) => {
    if (circleRef.current) circleRef.current.remove();

    const circle = L.circle(center, {
      radius,
      color: "#1976d2",
      fillColor: "#1976d2",
      fillOpacity: 0.12,
      weight: 2,
    }).addTo(map);

    // Make circle draggable on its edge via mousemove after mousedown on circle
    let dragging = false;

    circle.on("mousedown", (e: L.LeafletMouseEvent) => {
      dragging = true;
      map.dragging.disable();
      // Stop propagation so the map doesn't receive this as a click
      L.DomEvent.stopPropagation(e);
    });

    map.on("mousemove", (e: L.LeafletMouseEvent) => {
      if (!dragging || !centerRef.current) return;
      const newRadius = centerRef.current.distanceTo(e.latlng);
      circle.setRadius(newRadius);
      const lat = centerRef.current.lat;
      const lng = centerRef.current.lng;
      setInfo({ lat, lng, radius: Math.round(newRadius) });
      onChange(lat, lng, Math.round(newRadius));
    });

    map.on("mouseup", () => {
      if (dragging) {
        dragging = false;
        map.dragging.enable();
        // Leaflet fires a synthetic 'click' after mouseup — suppress it
        suppressNextClickRef.current = true;
      }
    });

    circleRef.current = circle;
  };

  const handleMapClick = (e: L.LeafletMouseEvent) => {
    const map = mapRef.current;
    if (!map) return;

    // Swallow the phantom click Leaflet emits right after a drag mouseup
    if (suppressNextClickRef.current) {
      suppressNextClickRef.current = false;
      return;
    }

    const step = stepRef.current;

    if (step === "idle" || step === "radius_set") {
      // Set new center — remove everything and start fresh
      if (circleRef.current) { circleRef.current.remove(); circleRef.current = null; }
      centerRef.current = e.latlng;
      drawMarker(map, e.latlng);
      stepRef.current = "center_set";
      setHint(hintText("center_set"));
      setInfo(null);
    } else if (step === "center_set") {
      // Set radius from distance
      const radius = Math.round(centerRef.current!.distanceTo(e.latlng));
      drawCircle(map, centerRef.current!, radius);
      stepRef.current = "radius_set";
      setHint(hintText("radius_set"));
      const lat = centerRef.current!.lat;
      const lng = centerRef.current!.lng;
      setInfo({ lat, lng, radius });
      onChange(lat, lng, radius);
    }
  };

  const handleReset = () => {
    const map = mapRef.current;
    if (!map) return;
    if (circleRef.current) { circleRef.current.remove(); circleRef.current = null; }
    if (markerRef.current) { markerRef.current.remove(); markerRef.current = null; }
    centerRef.current = null;
    stepRef.current = "idle";
    setHint(hintText("idle"));
    setInfo(null);
  };

  return (
    <div>
      <div
        style={{
          background: "#e3f2fd",
          border: "1px solid #90caf9",
          borderRadius: 6,
          padding: "6px 12px",
          marginBottom: 8,
          fontSize: 13,
          color: "#1565c0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>{hint}</span>
        <button
          onClick={handleReset}
          style={{
            background: "none",
            border: "1px solid #90caf9",
            borderRadius: 4,
            color: "#1565c0",
            cursor: "pointer",
            fontSize: 12,
            padding: "2px 8px",
          }}
        >
          Reset
        </button>
      </div>
      <div
        ref={containerRef}
        style={{ height: 380, width: "100%", borderRadius: 8, overflow: "hidden", border: "1px solid #dee2e6" }}
      />
      {info && (
        <div style={{ marginTop: 6, fontSize: 12, color: "#555", display: "flex", gap: 16 }}>
          <span>Lat: <strong>{info.lat.toFixed(6)}</strong></span>
          <span>Lng: <strong>{info.lng.toFixed(6)}</strong></span>
          <span>Radius: <strong>{info.radius.toLocaleString()} m</strong></span>
        </div>
      )}
    </div>
  );
};